import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Send, AlertTriangle } from "lucide-react";
import { studio } from "@/lib/studioApi";
import { Card } from "@/components/studio/StudioUI";

export const VerifactuCard = () => {
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tx, setTx] = useState([]);
  const load = () => Promise.all([studio.get("/verifactu/authorization"), studio.get("/verifactu/transmissions")]).then(([a, t]) => { setData(a.data); setTx(t.data); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const authorize = async () => {
    setBusy(true);
    try { await studio.post("/verifactu/authorization", { signer_name: name, accept }); toast.success("Autorización registrada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("¿Revocar la autorización a Devisto para el envío a la AEAT?")) return;
    await studio.delete("/verifactu/authorization"); toast.success("Autorización revocada"); load();
  };
  const badge = { accepted: "bg-emerald-100 text-emerald-800", error: "bg-red-100 text-red-700", queued: "bg-amber-100 text-amber-800", sending: "bg-blue-100 text-blue-800", cancelled: "bg-slate-100 text-slate-600" };

  return (
    <Card data-testid="verifactu-card" className="mt-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF2FF] text-[#3A5CC8]"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <p className="font-heading text-[15px] font-bold text-[#0F2645]">Verifactu · envío a la AEAT</p>
            <p className="mt-0.5 text-xs text-[#5C6A87]">NIF {data.nif || "—"} · {data.queue.accepted} registro(s) aceptado(s) · {data.queue.pending} pendiente(s)</p>
          </div>
        </div>
        {data.authorized && <button type="button" data-testid="verifactu-revoke" onClick={revoke} className="text-xs font-semibold text-[#5C6A87] hover:text-red-600">Revocar autorización</button>}
      </div>
      {!data.service_enabled && (
        <p data-testid="verifactu-pending-habilitation" className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> El envío a la AEAT se activará en cuanto la habilitación de Devisto como colaborador social esté validada. Mientras tanto, sus registros se generan y quedan en cola; se transmitirán automáticamente después.
        </p>
      )}
      {data.authorized ? (
        <p data-testid="verifactu-authorized" className="mt-3 text-sm text-[#0F2645]"><span className="font-semibold text-emerald-700">Autorización activa</span> · firmada por {data.authorization.signer_name} el {new Date(data.authorization.signed_at).toLocaleDateString("es-ES")} · huella {data.authorization.fingerprint.slice(0, 12)}…</p>
      ) : (
        <div className="mt-3 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
          <p className="text-xs leading-relaxed text-[#5C6A87]">{data.text}</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
            <input data-testid="verifactu-signer" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y apellidos del firmante" className="h-10 flex-1 rounded-lg border border-[#D9E0F2] bg-white px-3 text-sm" />
            <label className="inline-flex items-center gap-2 text-xs text-[#0F2645]"><input data-testid="verifactu-accept" type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="h-4 w-4 accent-[#3A5CC8]" /> Acepto</label>
            <button type="button" data-testid="verifactu-authorize" disabled={busy || !accept || name.trim().length < 3} onClick={authorize} className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#3A5CC8] px-4 text-sm font-semibold text-white disabled:opacity-50"><Send className="h-4 w-4" /> Autorizar a Devisto</button>
          </div>
        </div>
      )}
      {tx.length > 0 && (
        <ul className="mt-3 divide-y divide-[#EEF1F8] text-sm">
          {tx.slice(0, 6).map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-3 py-2">
              <span className="font-medium text-[#0F2645]">{t.numserie}</span>
              <span className="flex items-center gap-2 text-xs text-[#5C6A87]">{t.csv ? `CSV ${t.csv}` : t.response?.desc || ""}<span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${badge[t.status] || badge.cancelled}`}>{t.status}</span></span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
